import { Autocomplete, useTheme } from "@mui/material";
import { AutocompleteProps } from "@mui/material/Autocomplete/Autocomplete";
import { ChevronDownIcon, CloseSmallIcon } from "src/foundations/icons";
import { BaseDropdownOption, BaseDropdownProps } from "src/components/inputs/dropdown/commonTypes";
import { SenecTextField } from "src/components/inputs/input-field/SenecTextField";
import { forwardRef } from "react";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";

export type AutocompleteDropdownProps = {
    "data-testid"?: string;
    variant?: "outlined" | "filled";
    size?: "small" | "medium";
    required?: boolean;
} & BaseDropdownProps &
    Omit<
        AutocompleteProps<BaseDropdownOption | string, boolean | undefined, boolean | undefined, boolean | undefined>,
        "renderInput" | "onChange"
    >;

/**
 * Component for a custom drop down selection field with autocomplete/search functionality.
 * - Based on the MUI Autocomplete component.
 */
export const AutocompleteDropdown = forwardRef((props: AutocompleteDropdownProps, ref) => {
    const theme: SenecBaseTheme = useTheme();
    const { error, helperText, ...rest } = props;

    const handleOnChange = (value: NonNullable<string | BaseDropdownOption> | (string | BaseDropdownOption)[] | null) => {
        if (props.onChange) {
            if (value === null) {
                props.onChange(null);
            } else {
                props.onChange(typeof value === "string" ? value : (value as BaseDropdownOption).value);
            }
        }
    };

    return (
        <Autocomplete
            {...rest}
            ref={ref}
            value={props.value}
            options={props.options}
            onChange={(e, value) => handleOnChange(value)}
            popupIcon={<ChevronDownIcon key="popup-icon" sx={{ color: "text.primary", "&:disabled": { color: "red" } }} />}
            clearIcon={<CloseSmallIcon key="close-icon" sx={{ color: "senecSecondary.neutralGray40" }} />}
            data-testid={props["data-testid"] ?? "autocomplete"}
            sx={{
                ...theme.typography.s1,
                "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
                    pr: "40px !important",
                    fontWeight: 600,
                    ...theme.typography.s1Medium,
                },
                "& .MuiInputAdornment-root": {
                    position: "relative",
                    right: props.error ? "-15px" : "-40px",
                    bottom: props.size === "small" ? "0" : "5px",
                },
                ...props.sx,
            }}
            componentsProps={{
                paper: {
                    sx: {
                        fontSize: "14px !important",
                    },
                },
            }}
            renderInput={(params) => (
                <SenecTextField
                    {...params}
                    variant={props.variant}
                    size={props.size}
                    data-testid={"input-with-label"}
                    sx={{ input: { mr: "20px" } }}
                    error={error}
                    helperText={helperText}
                    label={props.label}
                    required={props.required}
                />
            )}
            isOptionEqualToValue={(option: string | BaseDropdownOption, value: string | BaseDropdownOption) =>
                props.isOptionEqualToValue
                    ? props.isOptionEqualToValue(option, value)
                    : typeof option === "string"
                      ? value === option
                      : (value as BaseDropdownOption).value === option.value
            }
        />
    );
});
